import * as React from 'react'
import { appProductName } from '../../appVariant'
import { useUsage } from '../../components/Paywall/useUsage'

// The API-access page doubles as the landing for signups that came from
// an integration (/signup?source=…). The key is still created by hand (no
// prefilled name — the typed name is an analytics signal); this header says
// why they are here and, once a key exists, how to plug it in.
const SURFACE = {
    obsidian: {
        where: 'in Obsidian',
        connect: () => 'Paste it in Settings → InfraNodus → API key.',
    },
    extension: {
        where: 'in the browser extension',
        connect: () => 'Paste it in the extension options.',
    },
    mcp: {
        where: 'from the MCP server',
        connect: null,
    },
    api: {
        where: 'through the API',
        connect: () =>
            `curl -H "Authorization: Bearer <your key>" ${window.location.origin}/api/v1/graphAndStatements?name=<graph>`,
    },
}

function McpConnect() {
    return (
        <ul className="tw-m-0 tw-list-disc tw-pl-5">
            <li>
                Using mcp.infranodus.com? Paste this key into the authorization
                window you came from.
            </li>
            <li>
                Running it locally? Add it to your config:
                <pre className="tw-mt-1 tw-overflow-x-auto tw-rounded-md tw-bg-muted tw-p-2 tw-text-xs">
                    {`{ "mcpServers": { "infranodus": { "command": "npx", "args": ["-y", "@infranodus/mcp"], "env": { "INFRANODUS_API_KEY": "<your key>" } } } }`}
                </pre>
            </li>
        </ul>
    )
}

export default function ApiAccessSourceHeader({ source, keyCreated }) {
    const { usage, isFree } = useUsage()
    const surface = SURFACE[source]
    if (!surface) return null
    const productName = appProductName()

    return (
        <div className="project-notes-scope">
            <section
                className="tw-mb-4 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card tw-p-4 tw-text-foreground"
                data-testid="api-access-source-header"
            >
                <h2 className="tw-m-0 tw-text-base tw-font-semibold">
                    {keyCreated
                        ? `Your key is ready — connect ${productName} ${surface.where}`
                        : `You're in. Create an API key to use ${productName} ${surface.where}`}
                </h2>
                {keyCreated ? (
                    <div
                        className="tw-mt-2 tw-text-sm"
                        data-testid="api-access-connect"
                    >
                        {surface.connect ? (
                            <p className="tw-m-0">{surface.connect()}</p>
                        ) : (
                            <McpConnect />
                        )}
                    </div>
                ) : (
                    <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-muted-foreground">
                        Name the key after what you connect, create it, and copy
                        it once — it is shown only at creation.
                    </p>
                )}
                {isFree &&
                typeof usage?.graphsCreated?.limit === 'number' &&
                typeof usage?.chatsStarted?.limit === 'number' ? (
                    <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-muted-foreground">
                        Free plan: {usage.graphsCreated.limit} graphs ·{' '}
                        {usage.chatsStarted.limit} AI chats. API calls have
                        their own limits.
                    </p>
                ) : null}
                <p className="tw-m-0 tw-mt-3 tw-text-sm">
                    <a href="/apps" className="tw-text-primary">
                        Open the web app
                    </a>
                </p>
            </section>
        </div>
    )
}
